import React, { useEffect, useCallback, useRef } from "react";
import { WysiwygEditor } from "@remirror/react-editors/wysiwyg";
import { useRemirrorContext, useHelpers, useDocChanged } from "@remirror/react";
import { DOMParser } from "@remirror/pm/model";
import RemirrorSectionRowToolbarPlugin from "./RemirrorSectionRowToolbarPlugin";

/**
 * Internal component that uses Remirror hooks (must be inside WysiwygEditor)
 */
function RemirrorEditorContent({ value, onChange }) {
  const { setContent, getHTML } = useHelpers();
  const { manager } = useRemirrorContext();
  const isInternalChange = useRef(false);
  const lastValue = useRef(value);

  // Convert HTML to ProseMirror and set content when value changes externally
  useEffect(() => {
    if (value !== undefined && manager.view && !isInternalChange.current) {
      try {
        const currentHTML = getHTML();
        // Only update if HTML has changed from external source
        if (currentHTML !== value && lastValue.current !== value) {
          // Parse HTML and convert to ProseMirror format
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = value || "";
          const schema = manager.view.state.schema;
          const domParser = DOMParser.fromSchema(schema);
          const node = domParser.parse(tempDiv);
          setContent(node);
          lastValue.current = value;
        }
      } catch (error) {
        console.error("Error parsing HTML:", error);
        // Fallback: set as plain text
        const text = value || "";
        setContent({
          type: "doc",
          content: [{ type: "paragraph", content: [{ type: "text", text }] }],
        });
        lastValue.current = value;
      }
    }
  }, [value, setContent, manager, getHTML]);

  // Handle content changes and convert back to HTML
  const handleChange = useCallback(() => {
    if (onChange && manager.view) {
      isInternalChange.current = true;
      const html = getHTML();
      onChange({ target: { value: html } });
      lastValue.current = html;
      setTimeout(() => {
        isInternalChange.current = false;
      }, 0);
    }
  }, [getHTML, onChange, manager]);

  // Propagate any document change (including style-only: bold, highlight, etc.) to parent
  useDocChanged(
    useCallback(
      ({ state }) => {
        if (onChange) {
          isInternalChange.current = true;
          const html = getHTML(state);
          onChange({ target: { value: html } });
          lastValue.current = html;
          setTimeout(() => {
            isInternalChange.current = false;
          }, 0);
        }
      },
      [onChange, getHTML]
    )
  );

  // Configure paste to be plain text only
  useEffect(() => {
    const view = manager.view;
    if (!view) return;

    const handlePaste = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const text = event.clipboardData?.getData("text/plain") || "";

      if (text) {
        const { state, dispatch } = view;
        const { selection } = state;
        const tr = state.tr.replaceSelectionWith(state.schema.text(text));
        dispatch(tr);
        setTimeout(() => handleChange(), 0);
        return true;
      }
      return false;
    };

    const dom = view.dom;
    dom.addEventListener("paste", handlePaste, true);

    return () => {
      dom.removeEventListener("paste", handlePaste, true);
    };
  }, [manager, handleChange]);

  // Listen to editor changes using DOM events and ProseMirror's update
  useEffect(() => {
    const view = manager.view;
    if (!view) return;

    const handleUpdate = () => {
      // Debounce to avoid too many updates
      setTimeout(() => handleChange(), 100);
    };

    // Listen to various DOM events that indicate content changes
    const events = ["input", "keyup", "cut", "paste"];
    events.forEach((eventType) => {
      view.dom.addEventListener(eventType, handleUpdate);
    });

    // Also listen to ProseMirror's update via a plugin
    const plugin = {
      view() {
        return {
          update: (view, prevState) => {
            if (prevState.doc !== view.state.doc) {
              handleUpdate();
            }
          },
        };
      },
    };

    // Note: We can't add plugins dynamically, so we rely on DOM events
    // The paste handler already calls handleChange, so this is mainly for other edits

    return () => {
      events.forEach((eventType) => {
        view.dom.removeEventListener(eventType, handleUpdate);
      });
    };
  }, [manager, handleChange]);

  return null;
}

/**
 * A wrapper around Remirror WysiwygEditor that:
 * - Converts HTML to/from ProseMirror format
 * - Handles plain text paste (strips formatting)
 * - Provides a simple HTML-based API
 * - Renders section row toolbar plugin with the other editor plugins
 */
export default function RemirrorEditor({
  value,
  onChange,
  placeholder,
  toolbarProps,
}) {
  return (
    <WysiwygEditor
      placeholder={placeholder}
      initialContent={value}
      stringHandler="html"
    >
      <RemirrorEditorContent value={value} onChange={onChange} />
      {toolbarProps && (
        <RemirrorSectionRowToolbarPlugin {...toolbarProps} inToolbar />
      )}
    </WysiwygEditor>
  );
}
